import { useEffect, useState } from 'react';
import { ActionModal, Modal } from 'components/ProfileUIComponents';
import { Flexbox, Text } from '@morressier/ts';
import { useForm } from 'react-hook-form';
import { ISubmissionSearchResult } from 'typings/submission';
import { IResearch } from 'typings/profile';
import { updateResearch } from 'clients/internal/profileService';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { KeyedMutator } from 'swr';
import { sentryCaptureException } from 'utils/utils';
import {
  Container,
  MediumInput
} from '../../Affiliations/EmailAffiliation/style';
import { StyledCheckbox, SecondaryText, AllCheckbox, Loader } from './style';
import { logEvent } from 'utils/eventTracking';

interface IOrganizationProps {
  isOpen: boolean;
  onDismiss: () => void;
  researchList: ISubmissionSearchResult[];
  userId: string;
  selectedResearch: string[];
  mutateResearchList: KeyedMutator<IResearch>;
}
interface FormValues {
  researchIds: string[];
}

const OrganizationsView = (props: IOrganizationProps) => {
  const {
    isOpen,
    onDismiss,
    researchList,
    userId,
    selectedResearch,
    mutateResearchList
  } = props;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm<FormValues>({ defaultValues: { researchIds: selectedResearch } });

  const [filteredResearch, setFilteredResearch] =
    useState<ISubmissionSearchResult[]>(researchList);
  const [showExitModal, setShowExitModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const closeExitModal = () => setShowExitModal(false);

  const openExitModal = () => setShowExitModal(true);

  const onSubmit = async (data: FormValues) => {
    try {
      setIsLoading(true);
      await updateResearch(userId, data.researchIds);
      mutateResearchList();
      logEvent('PROFILE_RESEARCH_MODAL_UPDATE_RESEARCH_CLICKED');
      // If the updated research list is set to none, track the 'PROFILE_RESEARCH_REMOVED' action.
      if (data.researchIds?.length === 0) logEvent('PROFILE_RESEARCH_REMOVED');
      onClose(data);
    } catch (err) {
      sentryCaptureException(err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterResearchList = (searchString: string) => {
    const filtered = researchList.filter(
      (orgOption: ISubmissionSearchResult) => {
        if (orgOption.title.toLowerCase().includes(searchString))
          return orgOption;
      }
    );
    setFilteredResearch(filtered);
  };

  const selectedResearchLength = watch('researchIds')?.length || 0;
  const onChangeAll = () => {
    if (
      selectedResearchLength !== researchList.length ||
      selectedResearchLength === 0
    ) {
      setValue(
        'researchIds',
        researchList.map(({ id }) => id)
      );
      logEvent('PROFILE_RESEARCH_MODAL_SELECT_ALL');
    } else if (selectedResearchLength === researchList.length) {
      setValue('researchIds', []);
      logEvent('PROFILE_RESEARCH_MODAL_UNSELECT_ALL');
    }
  };

  const onClose = (data?: FormValues) => {
    closeExitModal();
    onDismiss();
    reset(data?.researchIds ? data : undefined);
  };

  useEffect(() => {
    setFilteredResearch(researchList);
  }, [researchList]);

  const { onChange, onBlur, name, ref } = register('researchIds');

  const handleConfirmExitAction = () => {
    logEvent('PROFILE_RESEARCH_MODAL_EXIT_WITHOUT_SAVING_CONFIRM_CLICKED');
    onClose();
  };

  const handleCancelExitAction = () => {
    logEvent('PROFILE_RESEARCH_MODAL_EXIT_WITHOUT_SAVING_BACK_CLICKED');
    closeExitModal();
  };

  const handleExitWithOutSavingAction = () => {
    logEvent('PROFILE_RESEARCH_MODAL_EXIT_WITHOUT_SAVING_CLICKED');
    openExitModal();
  };

  const handleOutSideModalClick = () => {
    logEvent('PROFILE_RESEARCH_MODAL_DETAILS_CLICK_OUTSIDE_MODAL');
    openExitModal();
  };

  return (
    <ActionModal
      showExitContainer={showExitModal}
      onConfirmExitAction={handleConfirmExitAction}
      onCancelExitAction={handleCancelExitAction}
      onDismiss={handleOutSideModalClick}
      title="Add research"
      isOpen={isOpen}
      primaryAction={{
        title: 'Update research',
        type: 'submit',
        disabled: isLoading,
        iconAfter: isLoading ? <Loader icon={faSpinnerThird} /> : null,
        onClick: handleSubmit(onSubmit)
      }}
      cautionaryAction={{
        title: 'Exit without saving',
        onClick: handleExitWithOutSavingAction,
        type: 'button'
      }}
    >
      <Container>
        <Text size="h3_new" fontWeight="bold">
          Select the associated research you want to show on your profile
        </Text>
        <Text>
          Give visibility to your work by displaying associated research on your
          profile.
        </Text>
      </Container>
      <Container>
        <Text>Associated research found on the platform</Text>
        <MediumInput
          placeholder="Search by title"
          onChange={e => {
            logEvent('PROFILE_RESEARCH_MODAL_SEARCH_RESEARCH');
            filterResearchList(e.target.value.toLowerCase());
          }}
        />
        <Text>{filteredResearch?.length} results</Text>
      </Container>
      <AllCheckbox
        partialChecked={selectedResearchLength !== researchList.length}
        checked={selectedResearchLength > 0}
        onChange={onChangeAll}
      >
        {selectedResearchLength} selected research
      </AllCheckbox>
      <Flexbox flexDirection="column" gap="1rem">
        {filteredResearch?.map(item => (
          <StyledCheckbox
            key={item.id}
            value={item.id}
            onChange={e => {
              // If the research option is unchecked, track the 'PROFILE_RESEARCH_MODAL_UNSELECT_RECORD' event.
              if (!e?.target?.checked)
                logEvent('PROFILE_RESEARCH_MODAL_UNSELECT_RECORD');
              // If the research option is checked, track the 'PROFILE_RESEARCH_MODAL_SELECT_RECORD' event.
              if (e?.target?.checked)
                logEvent('PROFILE_RESEARCH_MODAL_SELECT_RECORD');

              onChange(e);
            }}
            onBlur={onBlur} // assign onBlur event
            name={name} // assign name prop
            ref={ref} // assign ref prop
          >
            <div>
              <Text fontWeight="bold">{item.title}</Text>
              {item.uploadedAt && (
                <SecondaryText>
                  Submitted at{' '}
                  {format(new Date(item.uploadedAt * 1000), 'MMM d, yyyy')}
                </SecondaryText>
              )}
              <SecondaryText>
                {item.authors[0].fullName}
                {item.authors.length > 1
                  ? ` and ${item.authors.length - 1} other authors`
                  : ''}
              </SecondaryText>
            </div>
          </StyledCheckbox>
        ))}
      </Flexbox>
    </ActionModal>
  );
};

export default OrganizationsView;
